import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Menu } from "primereact/menu";
import { Card } from "primereact/card";

import "../../../../layout/PrivateLayout/style.css";

const MOBILE_BREAKPOINT = 600;

const ArticleTable = ({
  articles: initialArticles = [],
  loading = false,
  onEdit,
  onDelete,
  onView,
}) => {
  const [articles, setArticles] = useState(initialArticles);
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT
  );
  const menusRef = useRef({});

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    
    let date;
    
    // Handle Firestore timestamp
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } 
    // Handle epoch time in milliseconds
    else if (typeof timestamp === 'number') {
      // If timestamp is in seconds, convert to milliseconds
      date = timestamp.toString().length === 10 
        ? new Date(timestamp * 1000) 
        : new Date(timestamp);
    }
    // Handle string timestamps
    else if (typeof timestamp === 'string') {
      // Try to parse the string as a date
      const parsedDate = new Date(timestamp);
      if (!isNaN(parsedDate.getTime())) {
        date = parsedDate;
      } else {
        return timestamp; // Return as is if we can't parse it
      }
    } else {
      return "N/A";
    }
    
    // Check if the time is midnight (00:00:00)
    if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
      // Format for date only
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else {
      // Format for date and time
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    }
  };

  // Update local state when initialArticles prop changes
  useEffect(() => {
    // Sort articles by updatedAt in descending order (newest first)
    const sortedArticles = [...initialArticles].sort((a, b) => {
      const aTime = a.updatedAt?.seconds ? a.updatedAt.seconds * 1000 : a.updatedAt;
      const bTime = b.updatedAt?.seconds ? b.updatedAt.seconds * 1000 : b.updatedAt;
      return (bTime || 0) - (aTime || 0);
    });
    setArticles(sortedArticles);
  }, [initialArticles]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleEdit = (id) => {
    const articleToEdit = articles.find((article) => article.id === id);
    if (articleToEdit) {
      onEdit(articleToEdit);
    }
  };

  const confirmDelete = (id) => {
    confirmDialog({
      message: "Are you sure you want to delete this article?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(id),
    });
  };

  const handleDelete = (id) => {
    // Optimistic UI update
    const originalArticles = [...articles];
    const updatedArticles = articles.filter((article) => article.id !== id);
    setArticles(updatedArticles);

    // Call the onDelete callback
    onDelete(id).catch((error) => {
      // Revert on error
      console.error("Error deleting article:", error);
      setArticles(originalArticles);
    });
  };

  const handleView = (id) => {
    const articleToView = articles.find((article) => article.id === id);
    if (articleToView && onView) {
      onView(articleToView);
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-info"
          onClick={() => handleView(rowData.id)}
          tooltip="View"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => handleEdit(rowData.id)}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => confirmDelete(rowData.id)}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  if (isMobile) {
    return (
      <div className="article-list">
        <ConfirmDialog />
        {loading && <div className="p-3 text-center">Loading articles...</div>}
        {!loading && (!articles || articles.length === 0) && (
          <div className="p-3 text-center">No articles found.</div>
        )}
        {articles.map((article) => {
          const menuModel = [
            {
              label: "Edit",
              icon: "pi pi-pencil",
              command: () => handleEdit(article.id),
            },
            {
              label: "Delete",
              icon: "pi pi-trash",
              command: () => confirmDelete(article.id),
            },
          ];
          return (
            <div
              key={article.id}
              className="article-list-item"
              onClick={() => handleView(article.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleView(article.id);
              }}
            >
              <div className="article-list-left">
                <div
                  className="article-title-ellipsis"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                  }}
                >
                  {article.title}
                </div>
                <div className="article-meta">
                  {formatDate(article.updatedAt)}
                </div>
              </div>
              <div
                className="article-list-right"
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className={`status-badge ${
                    article.status === "PUBLISHED" ? "published" : "draft"
                  }`}
                >
                  {article.status || "DRAFT"}
                </span>
                <Menu
                  model={menuModel}
                  popup
                  ref={(el) => {
                    menusRef.current[article.id] = el;
                  }}
                  id={`menu_${article.id}`}
                />
                <Button
                  icon="pi pi-ellipsis-v"
                  className="p-button-text p-button-rounded p-button-plain"
                  aria-haspopup
                  aria-controls={`menu_${article.id}`}
                  onClick={(event) =>
                    menusRef.current[article.id]?.toggle(event)
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card>
      <div className="card" style={{ overflowX: "auto" }}>
        <ConfirmDialog />
        <DataTable
          value={articles}
          paginator
          rows={10}
          loading={loading}
          loadingIcon="pi pi-spin pi-spinner"
          emptyMessage={loading ? "Loading articles..." : "No articles found."}
          className="p-datatable-sm"
          responsiveLayout="scroll"
        >
          <Column
            field="title"
            header="Title"
            sortable
            style={{ minWidth: "240px", maxWidth: "300px" }}
            body={(rowData) => (
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "280px",
                }}
              >
                {rowData.title}
              </div>
            )}
          />
          <Column
            field="templateType"
            header="Template Type"
            sortable
            headerClassName="col-hide-sm"
            bodyClassName="col-hide-sm"
            style={{ minWidth: "160px" }}
          />
          <Column
            field="status"
            header="Status"
            sortable
            style={{ minWidth: "130px" }}
          />
          <Column
            field="updatedAt"
            header="Last Updated"
            sortable
            headerClassName="col-hide-sm"
            bodyClassName="col-hide-sm"
            style={{ minWidth: "200px" }}
            body={(rowData) => formatDate(rowData.updatedAt)}
          />
          <Column
            body={actionBodyTemplate}
            style={{ minWidth: "120px", textAlign: "center" }}
          />
        </DataTable>
      </div>
    </Card>
  );
};

ArticleTable.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      templateType: PropTypes.string,
      status: PropTypes.string.isRequired,
      updatedAt: PropTypes.shape({
        seconds: PropTypes.number,
        nanoseconds: PropTypes.number,
      }),
    })
  ),
  loading: PropTypes.bool,
  // onEdit: Function called when edit button is clicked, receives the article object
  onEdit: PropTypes.func.isRequired,
  // onDelete: Function called when delete is confirmed, should return a Promise
  onDelete: PropTypes.func.isRequired,
  // onView: Function called when view button is clicked, receives the article object
  onView: PropTypes.func,
};

ArticleTable.defaultProps = {
  articles: [],
  loading: false,
};

export default ArticleTable;
